/* wd-new-pdp: SENSIA AIR product page redesign (phase 1).
   Custom elements used by the wd-pdp sections. Each element only touches its own subtree, so this
   file is inert on pages without them. They initialise themselves in connectedCallback, which also
   covers theme-editor section reloads. Rules: .claude/rules/new-pdp-scope.md */
(() => {
  if (window.wdNewPdp) return;
  window.wdNewPdp = { version: 1 };

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  /* In-page anchors inside our sections (#configurator, #logo-upload): smooth scroll, offset by the
     fixed or sticky bar (the store header) that covers the top of the viewport at click time. */
  const topOffset = () => {
    let offset = 0;
    document.elementsFromPoint(window.innerWidth / 2, 2).forEach((el) => {
      for (let node = el; node && node !== document.body; node = node.parentElement) {
        const position = getComputedStyle(node).position;
        if (position === 'fixed' || position === 'sticky') {
          offset = Math.max(offset, node.getBoundingClientRect().bottom);
          break;
        }
      }
    });
    return offset;
  };
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.wd-pdp a[href^="#"]');
    if (!link) return;
    const id = decodeURIComponent(link.getAttribute('href').slice(1));
    const target = id && document.getElementById(id);
    if (!target) return;
    event.preventDefault();
    const top = target.getBoundingClientRect().top + window.scrollY - topOffset();
    window.scrollTo({ top, behavior: reducedMotion() ? 'auto' : 'smooth' });
  });

  /* <wd-pdp-carousel>: native scroll-snap track, arrows scroll one card, disabled at both ends,
     progress follows the scroll position. Markup: [data-track], [data-prev], [data-next], [data-progress]. */
  class WdPdpCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.progress = this.querySelector('[data-progress]');
      this.onScroll = () => this.update();
      this.onPrev = () => this.step(-1);
      this.onNext = () => this.step(1);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.prev?.addEventListener('click', this.onPrev);
      this.next?.addEventListener('click', this.onNext);
      this.resizeObserver = new ResizeObserver(this.onScroll);
      this.resizeObserver.observe(this.track);
      this.update();
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
      this.prev?.removeEventListener('click', this.onPrev);
      this.next?.removeEventListener('click', this.onNext);
      this.resizeObserver?.disconnect();
    }

    step(direction) {
      const items = Array.from(this.track.children);
      if (!items.length) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const distance = items[0].getBoundingClientRect().width + gap;
      this.track.scrollBy({ left: direction * distance, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = Math.max(scrollWidth - clientWidth, 0);
      if (this.prev) this.prev.disabled = scrollLeft <= 1;
      if (this.next) this.next.disabled = scrollLeft >= max - 1;
      this.toggleAttribute('data-static', max <= 1);
      if (this.progress) {
        const size = scrollWidth ? Math.min(clientWidth / scrollWidth, 1) : 1;
        const ratio = max ? scrollLeft / max : 0;
        this.progress.style.setProperty('--wdp-progress-size', `${size * 100}%`);
        this.progress.style.setProperty('--wdp-progress-offset', `${ratio * ((1 - size) / size) * 100}%`);
      }
    }
  }
  define('wd-pdp-carousel', WdPdpCarousel);

  /* <wd-pdp-accordion>: buttons [aria-controls] toggle their panel; several can be open. */
  class WdPdpAccordion extends HTMLElement {
    connectedCallback() {
      this.onClick = (event) => {
        const button = event.target.closest('button[aria-controls]');
        if (!button || !this.contains(button)) return;
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        if (!panel) return;
        const open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(open));
        panel.hidden = !open;
      };
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }
  }
  define('wd-pdp-accordion', WdPdpAccordion);

  /* <wd-pdp-tabs>: [role=tab] buttons with aria-controls, arrow-key navigation. */
  class WdPdpTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.onClick = (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (tab && this.tabs.includes(tab)) this.select(tab);
      };
      this.onKey = (event) => {
        const index = this.tabs.indexOf(document.activeElement);
        if (index < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const last = this.tabs.length - 1;
        const nextIndex = { ArrowLeft: index ? index - 1 : last, ArrowRight: index === last ? 0 : index + 1, Home: 0, End: last }[event.key];
        this.select(this.tabs[nextIndex], true);
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKey);
    }

    select(tab, focus = false) {
      this.tabs.forEach((other) => {
        const active = other === tab;
        other.setAttribute('aria-selected', String(active));
        other.tabIndex = active ? 0 : -1;
        const panel = document.getElementById(other.getAttribute('aria-controls'));
        if (panel) panel.hidden = !active;
      });
      if (focus) tab.focus();
    }
  }
  define('wd-pdp-tabs', WdPdpTabs);

  /* <wd-pdp-video>: background video. Plays muted inline only while visible. Poster only with
     reduced motion or Save-Data. Picks the mobile or desktop source from data attributes. */
  class WdPdpVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      const saveData = navigator.connection && navigator.connection.saveData;
      if (reducedMotion() || saveData) return;
      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          this.load();
          this.video.play().catch(() => {});
        } else {
          this.video.pause();
        }
      }, { threshold: 0.1 });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    load() {
      if (this.loaded) return;
      this.loaded = true;
      const mobile = window.matchMedia('(max-width: 767px)').matches;
      const sources = this.querySelectorAll(mobile ? 'template[data-mobile]' : 'template[data-desktop]');
      const fallback = this.querySelectorAll('template[data-desktop], template[data-mobile]');
      const chosen = sources.length ? sources : fallback;
      if (!chosen.length) return;
      chosen[0].content.querySelectorAll('source').forEach((source) => this.video.appendChild(source.cloneNode(true)));
      this.video.load();
    }
  }
  define('wd-pdp-video', WdPdpVideo);

  /* <wd-pdp-readmore>: a [data-readmore-toggle] button reveals [data-readmore-content]. The text is in
     the HTML from the start. */
  class WdPdpReadmore extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-readmore-toggle]');
      this.content = this.querySelector('[data-readmore-content]');
      if (!this.button || !this.content) return;
      this.onClick = () => {
        const open = this.button.getAttribute('aria-expanded') !== 'true';
        this.button.setAttribute('aria-expanded', String(open));
        this.content.hidden = !open;
        const label = open ? this.button.dataset.labelClose : this.button.dataset.labelOpen;
        if (label) this.button.textContent = label;
      };
      this.button.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
    }
  }
  define('wd-pdp-readmore', WdPdpReadmore);

  /* <wd-pdp-buybar>: fixed to the bottom. Shown once [data-show-after] has scrolled out of view, and
     hidden while [data-hide-while] (the configurator) is in view. */
  class WdPdpBuybar extends HTMLElement {
    connectedCallback() {
      /* Selectors come from editor settings: an invalid one must not break the bar. */
      const find = (selector, fallback) => {
        try {
          return document.querySelector(selector || fallback);
        } catch (error) {
          return document.querySelector(fallback);
        }
      };
      /* Below 768px an optional data-show-after-mobile replaces data-show-after (e.g. the hero buttons). */
      const mobile = window.matchMedia('(max-width: 767px)').matches;
      this.afterEl = find((mobile && this.dataset.showAfterMobile) || this.dataset.showAfter, '.wd-pdp-hero');
      this.hideEl = find(this.dataset.hideWhile, '[data-section-type="product"]');
      this.state = { pastStart: !this.afterEl, hidden: false };
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.afterEl) {
            this.state.pastStart = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          }
          if (entry.target === this.hideEl) this.state.hidden = entry.isIntersecting;
        });
        this.toggleAttribute('data-visible', this.state.pastStart && !this.state.hidden);
      });
      [this.afterEl, this.hideEl].filter(Boolean).forEach((el) => this.observer.observe(el));
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }
  define('wd-pdp-buybar', WdPdpBuybar);
})();

/* >>>>> fragment 00-buy-bar.js >>>>> */
/* <wd-pdp-buybar-guard data-hide-while="<selector>">, placed inside <wd-pdp-buybar>: sets [data-suppressed] on the
   bar while any element matching the selector (default: the store footer, .wrapper-footer) is in view, so the fixed
   bar never covers the end of the page. It extends the bar without redefining it: <wd-pdp-buybar> keeps toggling
   [data-visible], and the CSS shows the bar only when [data-visible] is set and [data-suppressed] is not. */
(() => {
  if (customElements.get('wd-pdp-buybar-guard')) return;

  class WdPdpBuybarGuard extends HTMLElement {
    connectedCallback() {
      this.bar = this.closest('wd-pdp-buybar');
      const selector = this.dataset.hideWhile;
      let targets = [];
      try {
        targets = selector ? Array.from(document.querySelectorAll(selector)) : [];
      } catch (error) {
        targets = [];
      }
      if (!this.bar || !targets.length) return;
      this.inView = new Set();
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.inView.add(entry.target);
          else this.inView.delete(entry.target);
        });
        this.bar.toggleAttribute('data-suppressed', this.inView.size > 0);
      });
      targets.forEach((target) => this.observer.observe(target));
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.bar?.removeAttribute('data-suppressed');
    }
  }

  customElements.define('wd-pdp-buybar-guard', WdPdpBuybarGuard);
})();

/* Shopify's theme preview bar (#PBarNextFrameWrapper; only on ?preview_theme_id links, never on the live store) is fixed
   to the bottom of the screen in the browser's top layer, above every z-index, so it hid the bottom buy bar on phones.
   While it is on screen, every <wd-pdp-buybar> gets --wdp-buybar-offset = its height (and [data-above-preview]) and
   sits on top of it. The preview bar is third-party DOM injected after load: a childList-only MutationObserver on
   <body> waits for it, and a ResizeObserver follows it when it is collapsed or expanded. */
(() => {
  const bars = () => document.querySelectorAll('wd-pdp-buybar');
  const findPreviewBar = () => document.getElementById('PBarNextFrameWrapper') || document.getElementById('preview-bar-iframe');
  let watched = null;
  const update = () => {
    const previewBar = findPreviewBar();
    let offset = 0;
    if (previewBar) {
      const rect = previewBar.getBoundingClientRect();
      if (rect.height > 0 && rect.bottom >= window.innerHeight - 1) offset = Math.max(0, Math.round(window.innerHeight - rect.top));
      if (previewBar !== watched && 'ResizeObserver' in window) {
        watched = previewBar;
        new ResizeObserver(update).observe(previewBar);
      }
    }
    bars().forEach((bar) => {
      bar.style.setProperty('--wdp-buybar-offset', `${offset}px`);
      bar.toggleAttribute('data-above-preview', offset > 0);
    });
    /* The consent app's cookie button moves above the bar (sections/sc7-sticky-buy-bar.liquid): same offset. */
    document.getElementById('cc-toggle-btn')?.style.setProperty('--wdp-buybar-offset', `${offset}px`);
    /* HubSpot's chat launcher is injected later (after cookie consent): it inherits the offset from <html>. */
    document.documentElement.style.setProperty('--wdp-buybar-offset', `${offset}px`);
    return previewBar;
  };
  const start = () => {
    if (!bars().length) return;
    window.addEventListener('resize', update, { passive: true });
    if (update()) return;
    const observer = new MutationObserver(() => {
      if (update()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
/* <<<<< fragment 00-buy-bar.js <<<<< */

/* >>>>> fragment 01-usp-bar.js >>>>> */
/* <wd-pdp-usp-bar>: the USP top bar (sections/sc7-usp-bar.liquid). Below 1024px CSS shows only the item with
   [data-active] and runs a hold animation on it (none with reduced motion, paused while hovered or focused); at its
   animationend this moves [data-active] to the next USP. Focusing a USP link or selecting a block in the theme editor
   shows that USP. */
(() => {
  if (customElements.get('wd-pdp-usp-bar')) return;

  class WdPdpUspBar extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.wd-pdp-usp-bar__item'));
      if (this.items.length < 2) return;
      this.onEnd = (event) => {
        if (event.animationName !== 'wd-pdp-usp-hold' || !event.target.hasAttribute('data-active')) return;
        this.show((this.items.indexOf(event.target) + 1) % this.items.length);
      };
      this.onFocus = (event) => this.show(this.items.indexOf(event.target.closest('.wd-pdp-usp-bar__item')));
      this.onSelect = (event) => this.show(this.items.indexOf(event.target));
      this.addEventListener('animationend', this.onEnd);
      this.addEventListener('focusin', this.onFocus);
      this.addEventListener('shopify:block:select', this.onSelect);
      this.setAttribute('data-rotate', '');
    }

    show(index) {
      if (index < 0) return;
      this.items.forEach((item, i) => item.toggleAttribute('data-active', i === index));
    }

    disconnectedCallback() {
      this.removeEventListener('animationend', this.onEnd);
      this.removeEventListener('focusin', this.onFocus);
      this.removeEventListener('shopify:block:select', this.onSelect);
    }
  }

  customElements.define('wd-pdp-usp-bar', WdPdpUspBar);
})();
/* <<<<< fragment 01-usp-bar.js <<<<< */

/* >>>>> fragment 02-hero.js >>>>> */
/* 02 hero "Fill the first screen": --wdp-hero-top = the hero's distance from the top of the page (announcement bar +
   store header above it), so CSS can size it to the visible screen. Updated on resize and when the header group
   resizes. Inert without a .wd-pdp-hero--fill hero. */
(() => {
  const heroes = () => document.querySelectorAll('.wd-pdp-hero--fill');
  const update = () => {
    heroes().forEach((hero) => {
      const top = Math.max(0, Math.round(hero.getBoundingClientRect().top + window.scrollY));
      hero.style.setProperty('--wdp-hero-top', `${top}px`);
    });
  };
  const start = () => {
    if (!heroes().length) return;
    update();
    window.addEventListener('resize', update, { passive: true });
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(update);
      document.querySelectorAll('.shopify-section-group-header-group').forEach((el) => observer.observe(el));
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', update);
})();
/* <<<<< fragment 02-hero.js <<<<< */

/* >>>>> fragment 08-logo-upload.js >>>>> */
(() => {
/* 08 logo-upload: HubSpot's own file field is styled as a dropzone, with the native input stretched
     transparent over it (click and drag-and-drop stay native). These delegated listeners only add data
     attributes to the field wrapper: data-wdp-files = the chosen file names (shown by CSS ::after) and
     data-wdp-drag while a file is dragged over it. No child nodes are inserted into HubSpot's React tree, and
     nothing touches the value or the submission. Inert outside .wd-pdp sections.
     Self-contained: wrapped in its own IIFE when merged after the main one. */
  const fileDropzone = (node) => (node instanceof Element ? node.closest('.wd-pdp .hs-fieldtype-file') : null);

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'file') return;
    const zone = fileDropzone(input);
    if (!zone) return;
    const names = Array.from(input.files || [], (file) => file.name).join(', ');
    if (names) zone.setAttribute('data-wdp-files', names);
    else zone.removeAttribute('data-wdp-files');
  });

  document.addEventListener('dragenter', (event) => {
    fileDropzone(event.target)?.setAttribute('data-wdp-drag', '');
  });

  ['dragleave', 'drop', 'dragend'].forEach((type) => {
    document.addEventListener(type, (event) => {
      fileDropzone(event.target)?.removeAttribute('data-wdp-drag');
    });
  });
})();
/* <<<<< fragment 08-logo-upload.js <<<<< */

/* >>>>> fragment 11-logo-marquee.js >>>>> */
/* <wd-pdp-logos data-speed="30">: the logo rows of sections/sc7-wd-brand-slider.liquid move at a fixed speed in px
   per second (setting "Marquee speed") on every screen size: each row's loop duration = the width of its first set /
   speed, updated whenever that width changes (logos loading, breakpoints, theme editor). Without ResizeObserver the
   Liquid fallback duration stays. */
(() => {
  if (customElements.get('wd-pdp-logos')) return;

  class WdPdpLogos extends HTMLElement {
    connectedCallback() {
      const speed = parseFloat(this.dataset.speed);
      if (!(speed > 0) || !('ResizeObserver' in window)) return;
      this.observer = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const width = entry.contentRect.width;
          if (width > 0) entry.target.parentElement.style.setProperty('--wdp-marquee-duration', `${(width / speed).toFixed(2)}s`);
        });
      });
      this.querySelectorAll('.wd-pdp-marquee > .wd-pdp-marquee__set:first-child').forEach((set) => this.observer.observe(set));
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  customElements.define('wd-pdp-logos', WdPdpLogos);
})();
/* <<<<< fragment 11-logo-marquee.js <<<<< */

/* >>>>> fragment 16-faq.js >>>>> */
/* ===== 16 faq (sections/sc7-faq-tabs.liquid) ===== */
/* Theme editor only: selecting a FAQ block in the sidebar shows its tab and opens the question, so blocks in a
   hidden tab or a closed item become visible. Delegated, limited to .wd-pdp-faq. Uses WdPdpTabs#select. */
document.addEventListener('shopify:block:select', (event) => {
  const block = event.target;
  if (!(block instanceof Element) || !block.closest('.wd-pdp-faq')) return;
  const tabs = block.closest('wd-pdp-tabs');
  const panel = block.closest('[role="tabpanel"]');
  let tab = null;
  if (block.matches('[role="tab"]')) {
    tab = block;
  } else if (tabs && panel) {
    tab = tabs.querySelector(`[role="tab"][aria-controls="${CSS.escape(panel.id)}"]`);
  }
  if (tab && tabs && typeof tabs.select === 'function') tabs.select(tab);
  const trigger = block.querySelector('.wd-pdp-faq__trigger[aria-expanded="false"]');
  if (trigger) trigger.click();
});
/* ===== /16 faq ===== */
/* <<<<< fragment 16-faq.js <<<<< */

/* >>>>> fragment 18-product-slider.js >>>>> */
/* <wd-pdp-recommendations> (18 product-slider): with [data-url] (manual product list empty), loads this section
   from the Section Rendering API (/recommendations/products, intent=related) and swaps in the content of the
   element with the same id. No products or a failed request sets [data-empty]; CSS then hides the section.
   Without [data-url] the element is inert (cards are already server-rendered). */
(() => {
  if (customElements.get('wd-pdp-recommendations')) return;

  class WdPdpRecommendations extends HTMLElement {
    connectedCallback() {
      const url = this.dataset.url;
      if (!url || this.controller || this.hasAttribute('data-loaded') || this.hasAttribute('data-empty')) return;
      const controller = new AbortController();
      this.controller = controller;
      fetch(url, { signal: controller.signal })
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`))))
        .then((text) => {
          if (controller.signal.aborted) return;
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const source = this.id ? doc.getElementById(this.id) : null;
          const html = source && !source.hasAttribute('data-empty') ? source.innerHTML.trim() : '';
          this.setAttribute('data-loaded', '');
          if (html) {
            this.innerHTML = html;
          } else {
            this.setAttribute('data-empty', '');
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) this.setAttribute('data-empty', '');
        })
        .finally(() => {
          if (this.controller === controller) this.controller = null;
        });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }
  }

  customElements.define('wd-pdp-recommendations', WdPdpRecommendations);
})();
/* <<<<< fragment 18-product-slider.js <<<<< */
